import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function AuthModal({ open, onClose, setIsAuthenticated, setUser, onSuccess }) {
  const [contactType, setContactType] = useState("phone");
  const [contact, setContact] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pincode, setPincode] = useState("");
  // First-time email-signup capture: we want an SMS/WhatsApp channel for
  // booking confirmations, so phone is mandatory for new email accounts.
  const [signupPhone, setSignupPhone] = useState("");
  const [isReturningUser, setIsReturningUser] = useState(false);
  const [existingUserName, setExistingUserName] = useState("");
  const navigate = useNavigate();

  const handleSendOTP = async () => {
    if (!contact) {
      toast.error("Please enter your " + contactType);
      return;
    }

    setLoading(true);
    try {
      // Check if user already exists
      const checkRes = await axios.post(`${API}/auth/check-user`, { contact });
      if (checkRes.data.exists) {
        setIsReturningUser(true);
        setExistingUserName(checkRes.data.user?.name || "");
      } else {
        setIsReturningUser(false);
      }

      await axios.post(`${API}/auth/send-otp`, {
        contact: contact,
        contact_type: contactType
      });
      setOtpSent(true);
      toast.success("OTP sent successfully!");
    } catch (error) {
      toast.error("Failed to send OTP: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp) {
      toast.error("Please enter the OTP");
      return;
    }
    if (!isReturningUser && (!name || !pincode)) {
      toast.error("Please fill all required fields");
      return;
    }
    // First-time email signup must capture phone too (SMS/WhatsApp channel).
    if (!isReturningUser && contactType === "email") {
      const digits = (signupPhone || "").replace(/\D/g, "");
      if (digits.length < 10) {
        toast.error("Please enter a valid 10-digit phone number");
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        contact: contact,
        code: otp,
      };
      // Only send profile fields for new users
      if (!isReturningUser) {
        payload.name = name;
        payload.email = contactType === "email" ? contact : email;
        payload.pincode = pincode;
        // Normalise phone to E.164-ish "+91XXXXXXXXXX" when we received 10
        // digits — keeps it consistent with the phone-signup path which
        // already stores +91-prefixed numbers.
        if (contactType === "email") {
          const digits = (signupPhone || "").replace(/\D/g, "");
          payload.phone = digits.length === 10 ? `+91${digits}` : signupPhone;
        }
      }

      const response = await axios.post(`${API}/auth/verify-otp`, payload);

      if (response.data.valid) {
        // The backend has set an httpOnly auth cookie; no need to persist
        // the JWT in localStorage anymore.
        setIsAuthenticated(true);
        setUser(response.data.user);
        toast.success(isReturningUser ? `Welcome back, ${existingUserName || "traveler"}!` : "Account created successfully!");
        // Fully tear down the modal (clears internal state AND fires parent's
        // onClose). Defer the success callback so React commits the close
        // before the parent runs follow-up effects like navigate().
        handleClose();
        setTimeout(() => {
          if (onSuccess) {
            onSuccess();
          } else {
            navigate("/booking");
          }
        }, 0);
      } else {
        toast.error("Invalid OTP");
      }
    } catch (error) {
      toast.error("Verification failed: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setContact("");
    setOtp("");
    setOtpSent(false);
    setName("");
    setEmail("");
    setPincode("");
    setSignupPhone("");
    setIsReturningUser(false);
    setExistingUserName("");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="glass-card border-white/10 text-white max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <a href="/" data-testid="auth-logo-link">
            <img src="/logo.svg" alt="Travelsate" className="h-8 mx-auto mb-1" />
          </a>
          <p className="text-[#D4AF37]/70 text-[10px] tracking-widest uppercase text-center mb-3 font-bold italic">Book Now, Pay Later</p>
          <DialogTitle className="font-serif text-2xl text-center mb-2">Welcome</DialogTitle>
          <p className="text-sm text-white/60 text-center">Sign in to continue your journey</p>
        </DialogHeader>

        <Tabs
          value={contactType}
          onValueChange={(next) => {
            // Reset contact-bound state when switching tabs so the +91 phone
            // prefix doesn't leak into the email input (and vice versa).
            setContactType(next);
            setContact("");
            setOtp("");
            setOtpSent(false);
            setIsReturningUser(false);
            setExistingUserName("");
          }}
          className="mt-4"
        >
          <TabsList className="grid w-full grid-cols-2 bg-white/5 border border-white/10" data-testid="auth-tabs-list">
            <TabsTrigger
              value="phone"
              data-testid="phone-tab"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-white/70"
            >
              Phone
            </TabsTrigger>
            <TabsTrigger
              value="email"
              data-testid="email-tab"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-white/70"
            >
              Email
            </TabsTrigger>
          </TabsList>

          <TabsContent value="phone" className="space-y-4 mt-6">
            {!otpSent ? (
              <>
                <div>
                  <Label className="text-white/80">Phone Number</Label>
                  <div className="flex gap-2 mt-2">
                    <div className="flex items-center px-3 bg-white/5 border border-white/20 rounded-md text-white/60 text-sm">+91</div>
                    <Input
                      data-testid="phone-input"
                      type="tel"
                      placeholder="10 digit number"
                      maxLength={10}
                      value={contact.replace("+91", "")}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                        setContact("+91" + digits);
                      }}
                      className="bg-transparent border-white/20 text-white focus:border-[#D4AF37] flex-1"
                    />
                  </div>
                </div>
                <Button
                  data-testid="send-otp-button"
                  onClick={handleSendOTP}
                  disabled={loading}
                  className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C468]"
                >
                  {loading ? "Sending..." : "Send OTP"}
                </Button>
              </>
            ) : isReturningUser ? (
              <>
                <div className="text-center py-2">
                  <p className="text-white/80 text-sm">Welcome back, <span className="text-[#D4AF37] font-medium">{existingUserName}</span></p>
                </div>
                <div>
                  <Label className="text-white/80">Enter OTP</Label>
                  <Input
                    data-testid="otp-input"
                    type="text"
                    placeholder="6-digit OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className="bg-transparent border-white/20 text-white focus:border-[#D4AF37] mt-2"
                  />
                </div>
                <Button
                  data-testid="verify-otp-button"
                  onClick={handleVerifyOTP}
                  disabled={loading}
                  className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C468]"
                >
                  {loading ? "Verifying..." : "Verify & Login"}
                </Button>
              </>
            ) : (
              <>
                <div>
                  <Label className="text-white/80">Name</Label>
                  <Input
                    data-testid="name-input"
                    type="text"
                    placeholder="Your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="bg-transparent border-white/20 text-white focus:border-[#D4AF37] mt-2"
                  />
                </div>
                {contactType === "phone" && (
                  <div>
                    <Label className="text-white/80">Email (optional)</Label>
                    <Input
                      data-testid="email-input-optional"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="bg-transparent border-white/20 text-white focus:border-[#D4AF37] mt-2"
                    />
                  </div>
                )}
                <div>
                  <Label className="text-white/80">Pin Code</Label>
                  <Input
                    data-testid="pincode-input"
                    type="text"
                    placeholder="Your pin code"
                    value={pincode}
                    onChange={(e) => setPincode(e.target.value)}
                    className="bg-transparent border-white/20 text-white focus:border-[#D4AF37] mt-2"
                  />
                </div>
                <div>
                  <Label className="text-white/80">Enter OTP</Label>
                  <Input
                    data-testid="otp-input"
                    type="text"
                    placeholder="6-digit OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className="bg-transparent border-white/20 text-white focus:border-[#D4AF37] mt-2"
                  />
                </div>
                <Button
                  data-testid="verify-otp-button"
                  onClick={handleVerifyOTP}
                  disabled={loading}
                  className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C468]"
                >
                  {loading ? "Verifying..." : "Verify & Continue"}
                </Button>
              </>
            )}
          </TabsContent>

          <TabsContent value="email" className="space-y-4 mt-6">
            {!otpSent ? (
              <>
                <div>
                  <Label className="text-white/80">Email Address</Label>
                  <Input
                    data-testid="email-input"
                    type="email"
                    placeholder="your@email.com"
                    value={contact}
                    onChange={(e) => setContact(e.target.value)}
                    className="bg-transparent border-white/20 text-white focus:border-[#D4AF37] mt-2"
                  />
                </div>
                <Button
                  data-testid="send-otp-email-button"
                  onClick={handleSendOTP}
                  disabled={loading}
                  className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C468]"
                >
                  {loading ? "Sending..." : "Send OTP"}
                </Button>
              </>
            ) : isReturningUser ? (
              <>
                <div className="text-center py-2">
                  <p className="text-white/80 text-sm">Welcome back, <span className="text-[#D4AF37] font-medium">{existingUserName}</span></p>
                </div>
                <div>
                  <Label className="text-white/80">Enter OTP</Label>
                  <Input
                    data-testid="otp-input-email"
                    type="text"
                    placeholder="6-digit OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className="bg-transparent border-white/20 text-white focus:border-[#D4AF37] mt-2"
                  />
                </div>
                <Button
                  data-testid="verify-otp-email-button"
                  onClick={handleVerifyOTP}
                  disabled={loading}
                  className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C468]"
                >
                  {loading ? "Verifying..." : "Verify & Login"}
                </Button>
              </>
            ) : (
              <>
                <div>
                  <Label className="text-white/80">Name</Label>
                  <Input
                    data-testid="name-input-email"
                    type="text"
                    placeholder="Your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="bg-transparent border-white/20 text-white focus:border-[#D4AF37] mt-2"
                  />
                </div>
                <div>
                  <Label className="text-white/80">Phone Number</Label>
                  <div className="flex items-center mt-2">
                    <span className="bg-white/5 border border-white/20 border-r-0 rounded-l px-3 py-2 text-white/60 text-sm">+91</span>
                    <Input
                      data-testid="phone-input-email-signup"
                      type="tel"
                      placeholder="10-digit mobile"
                      value={signupPhone}
                      onChange={(e) => setSignupPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                      className="bg-transparent border-white/20 text-white focus:border-[#D4AF37] rounded-l-none"
                    />
                  </div>
                  <p className="text-white/40 text-[10px] mt-1">We&apos;ll use this to send booking confirmations &amp; SMS updates.</p>
                </div>
                <div>
                  <Label className="text-white/80">Pin Code</Label>
                  <Input
                    data-testid="pincode-input-email"
                    type="text"
                    placeholder="Your pin code"
                    value={pincode}
                    onChange={(e) => setPincode(e.target.value)}
                    className="bg-transparent border-white/20 text-white focus:border-[#D4AF37] mt-2"
                  />
                </div>
                <div>
                  <Label className="text-white/80">Enter OTP</Label>
                  <Input
                    data-testid="otp-input-email"
                    type="text"
                    placeholder="6-digit OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className="bg-transparent border-white/20 text-white focus:border-[#D4AF37] mt-2"
                  />
                </div>
                <Button
                  data-testid="verify-otp-email-button"
                  onClick={handleVerifyOTP}
                  disabled={loading}
                  className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C468]"
                >
                  {loading ? "Verifying..." : "Verify & Continue"}
                </Button>
              </>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}